import { useEffect, useState } from "react";
import type { SoftphoneState } from "../hooks/useSoftphone";

// ============================================================================
// SoftphonePanel — floating call widget for the browser softphone. Shows the
// live call state (connecting / ringing / active + duration), a recording dot,
// and mute / hangup controls. Audio flows through the browser mic/speakers via
// the Twilio Voice SDK; this is purely the on-screen control surface.
// ============================================================================

function fmtElapsed(startedAt: number | null, now: number): string {
  if (!startedAt) return "00:00";
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const STATUS_LABEL: Record<SoftphoneState["status"], string> = {
  idle: "",
  connecting: "Conectando…",
  ringing: "Llamando…",
  active: "En llamada",
  ended: "Llamada finalizada",
  error: "Error",
};

export default function SoftphonePanel({
  state,
  onHangup,
  onToggleMute,
  onClose,
}: {
  state: SoftphoneState;
  onHangup: () => void;
  onToggleMute: () => void;
  onClose: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());

  // Tick once a second while a call is live, to drive the duration timer.
  useEffect(() => {
    if (state.status !== "active") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state.status]);

  if (state.status === "idle") return null;

  const isLive = state.status === "active" || state.status === "ringing" || state.status === "connecting";
  const contact = state.lead?.name || state.lead?.company || state.lead?.whatsapp || "Lead";

  return (
    <div data-floating-corner="bottom-right" className="fixed bottom-6 right-6 z-[60] w-80 overflow-hidden rounded-2xl border border-line bg-card shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between bg-primary-500 px-4 py-3 text-on-primary">
        <div className="flex items-center gap-2">
          <i className="ri-phone-fill text-lg" />
          <span className="text-sm font-semibold">Llamada</span>
        </div>
        {isLive && state.status === "active" && (
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-300" /> grabando
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-4 text-center">
        <p className="text-base font-bold text-content">{contact}</p>
        {state.lead?.company && state.lead?.name && (
          <p className="text-xs text-content-muted">{state.lead.company}</p>
        )}

        <p className="mt-2 text-sm text-content-muted">
          {state.status === "error" ? (
            <span className="text-red-500">{state.error || "Error en la llamada"}</span>
          ) : (
            STATUS_LABEL[state.status]
          )}
        </p>

        {state.status === "active" && (
          <p className="mt-1 font-mono text-2xl font-bold text-content">
            {fmtElapsed(state.startedAt, now)}
          </p>
        )}

        {/* Controls */}
        <div className="mt-4 flex items-center justify-center gap-3">
          {(state.status === "active" || state.status === "ringing") && (
            <>
              <button
                onClick={onToggleMute}
                disabled={state.status !== "active"}
                title={state.muted ? "Reactivar micrófono" : "Silenciar"}
                className={`flex h-11 w-11 items-center justify-center rounded-full border text-lg transition ${
                  state.muted
                    ? "border-amber-500 bg-amber-500/10 text-amber-600"
                    : "border-line text-content-muted hover:bg-subtle"
                } disabled:opacity-40`}
              >
                <i className={state.muted ? "ri-mic-off-line" : "ri-mic-line"} />
              </button>
              <button
                onClick={onHangup}
                title="Colgar"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-xl text-white shadow-lg transition hover:bg-red-600"
              >
                <i className="ri-phone-fill rotate-[135deg]" />
              </button>
            </>
          )}

          {(state.status === "ended" || state.status === "error" || state.status === "connecting") && (
            <button
              onClick={state.status === "connecting" ? onHangup : onClose}
              className="rounded-lg border border-line px-4 py-2 text-sm text-content-muted hover:bg-subtle"
            >
              {state.status === "connecting" ? "Cancelar" : "Cerrar"}
            </button>
          )}
        </div>

        {state.status === "ended" && (
          <p className="mt-3 text-[11px] text-content-faint">
            La grabación, transcripción y calificación aparecerán en unos minutos.
          </p>
        )}
      </div>
    </div>
  );
}
